import * as React from "react";
import { useState } from "react";
import { TableCell, TableRow, Box, Checkbox } from "@mui/material";
import Moment from "react-moment";
import DriveService from "../../services/DriveService";
import FileService from "../../services/FileService";
import useDrive from "../../hooks/useDrive";
import useNotification from "../../hooks/useNotification";
import { DRIVE_TYPES } from "../../config/appConst";
import { FileIconSmall, getRowMeta } from "../FileThumbnail";
import styles from "./drive-row.module.css";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";

const FOLDER_COLORS = {
  Patients:    { bg: "rgba(251,191,36,0.15)",  icon: "#fbbf24" },
  "X-Rays":    { bg: "rgba(34,211,238,0.15)",  icon: "#22d3ee" },
  Reports:     { bg: "rgba(52,211,153,0.15)",  icon: "#34d399" },
  "3D-Models": { bg: "rgba(124,106,247,0.15)", icon: "#7c6af7" },
  default:     { bg: "rgba(124,106,247,0.15)", icon: "#7c6af7" },
};

function FolderIconBox({ name }) {
  const col = FOLDER_COLORS[name] || FOLDER_COLORS.default;
  return (
    <Box className={styles.iconWrap} sx={{ background: col.bg }}>
      <FolderOutlinedIcon sx={{ fontSize: 20, color: col.icon }} />
    </Box>
  );
}

function FileIconBox({ row }) {
  const { bg } = getRowMeta(row);
  return (
    <Box className={styles.iconWrap} sx={{ background: bg || "rgba(255,255,255,0.05)" }}>
      <FileIconSmall row={row} />
    </Box>
  );
}

export default function DriveRow({ row, selectMode, selected, onToggle }) {
  const [dragOver, setDragOver] = useState(false);
  const { CurrentDrive, changeCurrentDrive, changeSelectedRow, changePreviewObject } = useDrive();
  const { addNotification } = useNotification();

  const openFolder = async () => {
    const { parent, driveType } = CurrentDrive;
    const path = parent ? `${parent}/${row.Key}` : row.Key;
    const res = await DriveService.getFileTree(path, driveType);
    const cur = { parent: path, driveType };
    if (driveType === DRIVE_TYPES.SHARED) cur.sharedDrive = res; else cur.drive = res;
    changeCurrentDrive(cur);
  };

  const openFile = () => {
    changeSelectedRow(row);
    changePreviewObject(row);
  };

  const click = () => {
    if (selectMode) { onToggle?.(row.id); return; }
    row.type === "folder" ? openFolder() : openFile();
  };

  /* ── Drag source ── */
  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify({
      key: row.Key,
      type: row.type,
      parent: CurrentDrive.parent,
      driveType: CurrentDrive.driveType,
    }));
  };

  /* ── Drop target (folders only) ── */
  const handleDragOver = (e) => {
    if (row.type !== "folder") return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (row.type !== "folder") return;

    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData("application/json")); }
    catch { return; }

    if (payload.key === row.Key) return; // dropped on itself

    const { driveType } = CurrentDrive;
    const destFolder = CurrentDrive.parent
      ? `${CurrentDrive.parent}/${row.Key}`
      : row.Key;

    try {
      if (payload.type === "file") {
        const srcPath = payload.parent ? `${payload.parent}/${payload.key}` : payload.key;
        const dstPath = `${destFolder}/${payload.key}`;
        await FileService.copy({ file_name: srcPath, file_name_new: dstPath, move: 1, driveType });
      } else {
        const srcFolder = payload.parent ? `${payload.parent}/${payload.key}` : payload.key;
        await DriveService.copy({ folder: srcFolder, folder_new: destFolder, move: 1, driveType });
      }
      addNotification({ type: "success", message: `"${payload.key}" moved into "${row.Key}"` });
      const drive = await DriveService.getFileTree(CurrentDrive.parent, driveType);
      if (driveType === DRIVE_TYPES.SHARED)
        changeCurrentDrive({ ...CurrentDrive, drive: {}, sharedDrive: drive });
      else
        changeCurrentDrive({ ...CurrentDrive, drive, sharedDrive: {} });
    } catch (err) {
      addNotification({ type: "error", message: "Move failed" });
    }
  };

  const ext = row.type === "file" && row.Key
    ? row.Key.split(".").pop().toUpperCase()
    : null;

  const isDropTarget = row.type === "folder" && dragOver;

  return (
    <TableRow
      className={styles.driveRow}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        bgcolor: selected
          ? "rgba(0,122,255,0.12)"
          : isDropTarget
            ? "rgba(124,106,247,0.14)"
            : "transparent",
        outline: isDropTarget ? "2px solid #7c6af7" : "none",
        outlineOffset: "-2px",
        borderRadius: 1,
        cursor: "grab",
      }}
    >
      {/* Name column */}
      <TableCell sx={{ p: "0 !important", width: "55%" }}>
        <div
          className={styles.finderCell}
          onClick={click}
          style={{
            cursor: "pointer",
            background: selected ? "rgba(0,122,255,0.12)" : isDropTarget ? "rgba(124,106,247,0.1)" : undefined,
          }}
        >
          {selectMode && (
            <Checkbox
              checked={!!selected}
              onChange={() => onToggle?.(row.id)}
              onClick={e => e.stopPropagation()}
              size="small"
              sx={{ p: 0, mr: 0.5, color: "rgba(255,255,255,0.3)", "&.Mui-checked": { color: "#0a84ff" } }}
            />
          )}
          {row.type === "folder" ? <FolderIconBox name={row.Key} /> : <FileIconBox row={row} />}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className={styles.itemName}>{row.Key}</div>
          </div>
        </div>
      </TableCell>

      {/* Kind */}
      <TableCell sx={{ p: "0 !important", width: "12%" }}>
        <div className={styles.colValue} onClick={click} style={{ cursor: "pointer" }}>
          {row.type === "folder" ? "Folder" : (ext || "—")}
        </div>
      </TableCell>

      {/* Size */}
      <TableCell sx={{ p: "0 !important", width: "13%" }}>
        <div className={styles.colValue} onClick={click} style={{ cursor: "pointer" }}>
          {row.Size || "—"}
        </div>
      </TableCell>

      {/* Date modified */}
      <TableCell sx={{ p: "0 !important", width: "20%" }}>
        <div className={styles.colValue} onClick={click} style={{ cursor: "pointer" }}>
          {row.LastModified
            ? <Moment format="MMM D, YYYY h:mm a">{row.LastModified}</Moment>
            : "—"}
        </div>
      </TableCell>
    </TableRow>
  );
}
